import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CARD_SUITS, FACE_CARDS, JOKERS } from "@/data/cards";
import { AnimatedWords } from "./HeroSection";
import { ShoppingCart } from "lucide-react";
import { useCopy } from "@/lib/copyVariant";
import { img } from "@/lib/img";

const suitTabs = [
  { value: "diamonds", label: "\u2666 Diamonds" },
  { value: "hearts", label: "\u2665 Hearts" },
  { value: "spades", label: "\u2660 Spades" },
  { value: "clubs", label: "\u2663 Clubs" },
  { value: "royals", label: "Royals" },
];

const MAX_PER_TAB = 4;
const scrollToBuy = () => document.querySelector("#begin-journey")?.scrollIntoView({ behavior: "smooth", block: "start" });

function CardItem({ card, index }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.92 }} whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }} transition={{ delay: index * 0.05, duration: 0.4 }}>
      <div className="card-lift bg-white border border-[#464646]/8 rounded-xl sm:rounded-2xl p-1.5 sm:p-2 cursor-pointer hover:border-[#96aeb4]/40">
        <img src={img(card.url, { w: 480 })} alt={card.name} loading="lazy" decoding="async" width="320" height="448" className="w-full rounded-lg sm:rounded-xl"
          data-testid={`card-${card.name.toLowerCase().replace(/\s+/g, "-")}`} />
      </div>
      <p className="text-[9px] sm:text-[10px] text-center mt-1.5 text-[#464646]/55">{card.name}</p>
    </motion.div>
  );
}

export default function CardGallery() {
  const { c } = useCopy();
  return (
    <section id="gallery" className="py-16 sm:py-24 md:py-36 px-5 md:px-10 relative bg-[#fff8f3]" data-testid="card-gallery-section">
      <img src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/vdsn4td1_65cc8282f19e9ebb31a99e91_Group%2026968.avif", { w: 500 })}
        alt="" aria-hidden="true" loading="lazy" decoding="async" width="208" height="208" className="absolute top-8 right-0 w-32 md:w-52 opacity-30 pointer-events-none" />
      <div className="max-w-[1400px] mx-auto relative">
        <motion.span className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-5"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>{c("galleryEyebrow")}</motion.span>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#464646] leading-[1.1] tracking-tight mb-4">
          <AnimatedWords text={c("galleryHeading")} />
        </h2>
        <motion.p className="text-xs sm:text-sm text-[#464646]/60 mb-10 sm:mb-14 max-w-md"
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
          {c("galleryBody")}
        </motion.p>

        <Tabs defaultValue="diamonds" className="w-full" data-testid="card-tabs">
          <div className="overflow-x-auto pb-2 -mx-1">
            <TabsList className="mb-6 sm:mb-8 bg-white border border-[#464646]/8 p-1 sm:p-1.5 rounded-full inline-flex gap-0.5 sm:gap-1 h-auto min-w-max shadow-sm">
              {suitTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} data-testid={`tab-${tab.value}`}
                  className="rounded-full px-3 sm:px-5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-medium text-[#464646]/60 transition-all data-[state=active]:bg-[#464646] data-[state=active]:text-white">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {Object.entries(CARD_SUITS).map(([suit, cards]) => (
            <TabsContent key={suit} value={suit}>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5 max-w-3xl">
                {cards.slice(0, MAX_PER_TAB).map((c, i) => <CardItem key={c.name} card={c} index={i} />)}
              </div>
            </TabsContent>
          ))}
          <TabsContent value="royals">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4 md:gap-5 max-w-3xl">
              {[...FACE_CARDS, ...JOKERS].slice(0, MAX_PER_TAB).map((c, i) => <CardItem key={c.name} card={c} index={i} />)}
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA - guide users toward purchase */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-between gap-5 p-5 sm:p-8 rounded-2xl bg-white border border-[#464646]/8 shadow-sm">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#96aeb4] font-semibold mb-1">{c("galleryBottomEyebrow")}</p>
            <h3 className="font-serif text-xl sm:text-2xl text-[#464646] font-medium">{c("galleryBottomTitle")}</h3>
            <p className="text-xs sm:text-sm text-[#464646]/60 mt-1">{c("galleryBottomBody")}</p>
          </div>
          <button onClick={scrollToBuy} data-testid="gallery-buy-cta"
            className="bg-[#464646] hover:bg-[#96aeb4] text-white rounded-full px-7 py-3.5 text-sm font-semibold transition-all shadow-md flex items-center gap-2 whitespace-nowrap">
            <ShoppingCart size={16} /> {c("heroSecondaryCta")}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
